import { useEffect, useRef } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Card } from "@volca/ui/components/ui/card";
import { Stack } from "@volca/ui/components/ui/stack";
import { Heading, Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import { ReportSlotPicker } from "@/profitability/report-slot-picker";
import type {
  ProfitabilityDocument,
  ReportInputSlot,
} from "@/profitability/types";

/**
 * Not a file the report reads: the owner's own figures for the period (their
 * spreadsheet), which the run is checked against once it completes, within
 * the template's "close enough" (Settings).
 */
export const GOAL_SLOT: ReportInputSlot = {
  key: "goal",
  title: "Your numbers for this period (optional)",
  description:
    "Add your own spreadsheet for these dates and Volca checks the report's figures against it once it runs.",
  sheets: [],
};

export const RunInputCard = ({
  businessId,
  slot,
  document,
  onDocumentChange,
  wanted = null,
  kept = null,
  onUseKept,
}: {
  businessId: string;
  slot: ReportInputSlot;
  document: ProfitabilityDocument | null;
  onDocumentChange: (document: ProfitabilityDocument | null) => void;
  /**
   * Why this is the file to add, when a missing figure sent the owner here
   * ("Labor for Oct 12–18 comes from this file."): the card is marked and
   * scrolled into view.
   */
  wanted?: string | null;
  /** A reference slot's kept file, when the owner removed it to add another. */
  kept?: ProfitabilityDocument | null;
  onUseKept?: () => void;
}) => {
  const card = useRef<HTMLDivElement>(null);
  const marked = !!wanted;
  useEffect(() => {
    if (marked) card.current?.scrollIntoView({ block: "center" });
  }, [marked]);
  return (
    <Card
      ref={card}
      data-wanted={wanted ? "" : undefined}
      className={cn("min-w-0 gap-6 p-6", {
        "ring-2 ring-warning-line": !!wanted,
      })}
    >
      <Stack gap={2}>
        <Heading level={2}>{slot.title}</Heading>
        {slot.description && <Text tone="muted">{slot.description}</Text>}
        {slot.reference && (
          <Text tone="muted">
            Kept from when the agent was set up. Add a new file only when it
            changes.
          </Text>
        )}
        {wanted && (
          <Text className="inline-flex items-center gap-2">
            <span
              aria-hidden
              className="size-1.5 flex-none rounded-full bg-warning"
            />
            {wanted}
          </Text>
        )}
      </Stack>
      {slot.sheets.length > 0 && (
        <details className="min-w-0">
          <summary className="cursor-pointer rounded-md text-sm text-link focus-visible:outline-2 focus-visible:outline-focus">
            File requirements
          </summary>
          <Stack gap={3} className="pt-4">
            {slot.sheets.map((sheet) => (
              <Stack key={sheet.name} gap={1}>
                <Text variant="label" className="break-words">
                  {sheet.name}
                </Text>
                <Text tone="muted" className="break-words">
                  {sheet.columns.length
                    ? `Columns: ${sheet.columns.join(", ")}`
                    : "Include this sheet in the file."}
                </Text>
              </Stack>
            ))}
          </Stack>
        </details>
      )}
      <ReportSlotPicker
        businessId={businessId}
        slot={slot}
        document={document}
        onDocumentChange={onDocumentChange}
        showDescription={false}
        buttonLabel="Add file"
      />
      {kept && onUseKept && !document && (
        <Button variant="ghost" className="self-start" onClick={onUseKept}>
          Use the kept file ({kept.file_name})
        </Button>
      )}
    </Card>
  );
};
